import type { TextosDoSegredoExibido } from '@/shared/components/ui';

import type {
  AbaDaCentral,
  AmbienteDoDestino,
  BlocoDeEventos,
  EstadoDoEvento,
  MotivoDaRecusaDoReenvio,
  Predefinicao,
  ProblemaDaCredencial,
  ProblemaDaUrl,
  SituacaoDoDestino,
  TipoDeAutenticacao,
  TipoDeEvento,
} from '../types/centralWebhooksTypes';

/**
 * Catálogo da v1: os 12 de arbitragem (D6a-2 e D6a-6), os 14 de negociação e os 16 de notificação.
 * Títulos e descrições da arbitragem vêm do protótipo `central-webhooks.logic.js`;
 * `pending_action.expired` não existe lá e `pending_action.updated` segue a D6a-6 (reabertura,
 * prorrogação, recusa da validação). Os de negociação e de notificação não têm protótipo: textos
 * escritos a partir do catálogo do core.
 */
export const BLOCOS_DE_EVENTOS: readonly BlocoDeEventos[] = [
  {
    produto: 'arbitration',
    titulo: 'Ciclo de vida do processo',
    eventos: [
      {
        tipo: 'arbitration.created',
        titulo: 'Processo criado',
        descricao: 'O processo nasce na Arbitralis, pela API ou pela plataforma.',
      },
      {
        tipo: 'arbitration.status_changed',
        titulo: 'Situação do processo alterada',
        descricao: 'O processo muda de etapa ou situação.',
      },
      {
        tipo: 'arbitration.closed',
        titulo: 'Processo encerrado',
        descricao: 'O processo chega ao encerramento formal.',
      },
    ],
  },
  {
    produto: 'arbitration',
    titulo: 'Andamentos',
    eventos: [
      {
        tipo: 'arbitration.update.created',
        titulo: 'Novo andamento publicado',
        descricao: 'Qualquer andamento visível ao requerente entra no processo.',
      },
    ],
  },
  {
    produto: 'arbitration',
    titulo: 'Pendências do requerente',
    eventos: [
      {
        tipo: 'arbitration.pending_action.created',
        titulo: 'Nova pendência',
        descricao: 'Uma ação dirigida ao requerente é criada.',
      },
      {
        tipo: 'arbitration.pending_action.updated',
        titulo: 'Pendência atualizada',
        descricao:
          'A pendência é reaberta, tem o prazo prorrogado ou a resposta é recusada na validação.',
      },
      {
        tipo: 'arbitration.pending_action.answered',
        titulo: 'Pendência respondida',
        descricao: 'O requerente envia a resposta.',
      },
      {
        tipo: 'arbitration.pending_action.completed',
        titulo: 'Pendência concluída',
        descricao: 'A secretaria analisa e encerra a pendência.',
      },
      {
        tipo: 'arbitration.pending_action.expired',
        titulo: 'Pendência expirada',
        descricao: 'O prazo da pendência vence sem resposta, ou o processo é encerrado antes.',
      },
    ],
  },
  {
    produto: 'arbitration',
    titulo: 'Documentos e decisão',
    eventos: [
      {
        tipo: 'arbitration.document.available',
        titulo: 'Novo documento disponível',
        descricao: 'Um documento visível ao requerente fica disponível.',
      },
      {
        tipo: 'arbitration.award.published',
        titulo: 'Sentença arbitral publicada',
        descricao: 'A decisão é disponibilizada oficialmente.',
      },
      {
        tipo: 'arbitration.final_and_binding',
        titulo: 'Trânsito em julgado registrado',
        descricao: 'Encerra o prazo de manifestação e a decisão se torna definitiva.',
      },
    ],
  },
  {
    produto: 'negotiation',
    titulo: 'Ciclo de vida da negociação',
    eventos: [
      {
        tipo: 'negotiation.started',
        titulo: 'Negociação iniciada',
        descricao: 'O primeiro contato com a contraparte é disparado.',
      },
      {
        tipo: 'negotiation.counterparty_responded',
        titulo: 'Contraparte respondeu',
        descricao: 'A contraparte responde pela primeira vez e a negociação fica ativa.',
      },
      {
        tipo: 'negotiation.paused',
        titulo: 'Negociação pausada',
        descricao: 'A negociação é pausada e o agente para de atuar.',
      },
      {
        tipo: 'negotiation.resumed',
        titulo: 'Negociação retomada',
        descricao: 'A negociação pausada volta a andar.',
      },
      {
        tipo: 'negotiation.closed',
        titulo: 'Negociação encerrada',
        descricao: 'A negociação termina, com ou sem acordo, por prazo, cancelamento ou falha.',
      },
      {
        tipo: 'negotiation.reopened',
        titulo: 'Negociação reaberta',
        descricao: 'Uma negociação encerrada volta a ficar ativa.',
      },
      {
        tipo: 'negotiation.situation_changed',
        titulo: 'Situação alterada',
        descricao:
          'Uma situação da negociação é adicionada ou removida, pela equipe ou pelo sistema.',
      },
    ],
  },
  {
    produto: 'negotiation',
    titulo: 'Acordo',
    eventos: [
      {
        tipo: 'negotiation.agreement.reached',
        titulo: 'Acordo fechado',
        descricao: 'A negociação é encerrada com acordo.',
      },
      {
        tipo: 'negotiation.agreement.sent_for_signature',
        titulo: 'Contrato enviado para assinatura',
        descricao: 'O contrato do acordo é enviado ao provedor de assinatura.',
      },
      {
        tipo: 'negotiation.agreement.first_signer_signed',
        titulo: 'Primeira assinatura',
        descricao: 'O primeiro signatário assina o contrato do acordo.',
      },
      {
        tipo: 'negotiation.agreement.signed',
        titulo: 'Contrato assinado',
        descricao: 'Todos os signatários assinam o contrato do acordo.',
      },
      {
        tipo: 'negotiation.agreement.signature_stalled',
        titulo: 'Assinatura parada',
        descricao: 'O prazo de assinatura vence sem todas as assinaturas.',
      },
    ],
  },
  {
    produto: 'negotiation',
    titulo: 'Lotes de negociação',
    eventos: [
      {
        tipo: 'negotiation.batch.completed',
        titulo: 'Lote concluído',
        descricao: 'Todas as negociações do lote foram processadas sem erro.',
      },
      {
        tipo: 'negotiation.batch.failed',
        titulo: 'Lote com falha',
        descricao: 'O lote termina com itens com erro ou não pôde ser processado.',
      },
    ],
  },
  {
    produto: 'notification',
    titulo: 'Ciclo de vida da notificação',
    eventos: [
      {
        tipo: 'notification.created',
        titulo: 'Notificação criada',
        descricao: 'A notificação é criada, avulsa ou num lote.',
      },
      {
        tipo: 'notification.status_changed',
        titulo: 'Status alterado',
        descricao:
          'O status geral da notificação muda (enviada, entregue, visualizada, cancelada).',
      },
      {
        tipo: 'notification.accessed',
        titulo: 'Notificado acessou',
        descricao: 'O notificado abre a notificação no portal pela primeira vez.',
      },
      {
        tipo: 'notification.reply.received',
        titulo: 'Resposta recebida',
        descricao: 'O notificado responde, se manifesta ou envia um comprovante pelo portal.',
      },
      {
        tipo: 'notification.certificate.available',
        titulo: 'Certificado de acessos gerado',
        descricao: 'Uma versão nova do certificado de acessos da notificação é gerada.',
      },
    ],
  },
  {
    produto: 'notification',
    titulo: 'Canais digitais',
    eventos: [
      {
        tipo: 'notification.channel.sent',
        titulo: 'Mensagem enviada',
        descricao: 'O e-mail, o WhatsApp ou o RCS é aceito pelo provedor.',
      },
      {
        tipo: 'notification.channel.delivered',
        titulo: 'Mensagem entregue',
        descricao: 'O provedor confirma a entrega no canal.',
      },
      {
        tipo: 'notification.channel.read',
        titulo: 'Mensagem lida',
        descricao: 'O provedor confirma a leitura (WhatsApp, RCS) ou o clique no link (e-mail).',
      },
      {
        tipo: 'notification.channel.failed',
        titulo: 'Falha no canal',
        descricao: 'O envio falha, volta (bounce), cai em spam ou é barrado antes de sair.',
      },
    ],
  },
  {
    produto: 'notification',
    titulo: 'Carta',
    eventos: [
      {
        tipo: 'notification.letter.posted',
        titulo: 'Carta postada',
        descricao: 'A carta é postada nos Correios.',
      },
      {
        tipo: 'notification.letter.delivered',
        titulo: 'Carta entregue',
        descricao: 'Os Correios confirmam a entrega da carta.',
      },
      {
        tipo: 'notification.letter.returned',
        titulo: 'Carta devolvida',
        descricao: 'A carta volta: recusada, endereço inválido ou não entregue.',
      },
    ],
  },
  {
    produto: 'notification',
    titulo: 'Lotes de notificação',
    eventos: [
      {
        tipo: 'notification.batch.confirmed',
        titulo: 'Lote confirmado',
        descricao: 'O envio do lote é confirmado, para agora ou para a data agendada.',
      },
      {
        tipo: 'notification.batch.completed',
        titulo: 'Lote enviado',
        descricao: 'Todas as notificações do lote foram disparadas.',
      },
      {
        tipo: 'notification.batch.canceled',
        titulo: 'Lote cancelado',
        descricao: 'O lote e as notificações dele são cancelados.',
      },
      {
        tipo: 'notification.batch.failed',
        titulo: 'Lote com erro',
        descricao: 'O processamento do arquivo do lote falha.',
      },
    ],
  },
];

export const EVENTOS_DA_V1: readonly TipoDeEvento[] = BLOCOS_DE_EVENTOS.flatMap((bloco) =>
  bloco.eventos.map((evento) => evento.tipo),
);

/** Por texto, não por `TipoDeEvento`: um tipo que o core passe a mandar aparece cru. */
export const TITULO_DO_EVENTO: Readonly<Record<string, string>> = Object.fromEntries(
  BLOCOS_DE_EVENTOS.flatMap((bloco) => bloco.eventos.map((evento) => [evento.tipo, evento.titulo])),
);

export const OPCOES_DE_PREDEFINICAO: readonly {
  valor: Predefinicao;
  rotulo: string;
  descricao: string;
}[] = [
  {
    valor: 'ESSENCIAL',
    rotulo: 'Essencial',
    descricao: 'Ciclo do processo, novas pendências, documentos e decisão.',
  },
  {
    valor: 'OPERACIONAL',
    rotulo: 'Operacional',
    descricao: 'Essencial mais andamentos e o acompanhamento de cada pendência.',
  },
  { valor: 'COMPLETO', rotulo: 'Completo', descricao: 'Todos os eventos da Arbitragem.' },
];

export const ABAS_DA_CENTRAL: readonly { valor: AbaDaCentral; rotulo: string }[] = [
  { valor: 'INTEGRACAO', rotulo: 'Integração' },
  { valor: 'EVENTOS', rotulo: 'Eventos' },
  { valor: 'AUDITORIA', rotulo: 'Auditoria' },
];

export const ROTULO_DO_AMBIENTE: Record<AmbienteDoDestino, string> = {
  HOMOLOGACAO: 'Homologação',
  PRODUCAO: 'Produção',
};

export const AMBIENTES: readonly AmbienteDoDestino[] = ['HOMOLOGACAO', 'PRODUCAO'];

export const ROTULO_DA_SITUACAO: Record<SituacaoDoDestino, string> = {
  RASCUNHO: 'Rascunho',
  ATIVA: 'Ativa',
  PAUSADA: 'Pausada',
  COM_FALHA: 'Com falha',
};

export const VARIANTE_DA_SITUACAO: Record<
  SituacaoDoDestino,
  'neutral' | 'success' | 'warning' | 'error'
> = {
  RASCUNHO: 'neutral',
  ATIVA: 'success',
  PAUSADA: 'warning',
  COM_FALHA: 'error',
};

export const ROTULO_DA_AUTENTICACAO: Record<TipoDeAutenticacao, string> = {
  NENHUMA: 'Sem autenticação',
  BEARER: 'Bearer token',
  BASIC: 'Basic auth',
  CABECALHO: 'Cabeçalho personalizado',
};

export const ORDEM_DA_AUTENTICACAO: readonly TipoDeAutenticacao[] = [
  'NENHUMA',
  'BEARER',
  'BASIC',
  'CABECALHO',
];

export const MENSAGEM_DO_PROBLEMA_DA_URL: Record<ProblemaDaUrl, string> = {
  VAZIA: 'Informe a URL que vai receber os eventos.',
  INVALIDA: 'URL inválida. Ex.: https://sistema.escritorio.com.br/webhooks',
  SEM_HTTPS: 'Use um endereço https://.',
  COM_CREDENCIAL: 'Não coloque usuário e senha na URL: use a autenticação abaixo.',
};

export const MENSAGEM_DO_PROBLEMA_DA_CREDENCIAL: Record<ProblemaDaCredencial, string> = {
  TOKEN_VAZIO: 'Informe o token.',
  USUARIO_OU_SENHA_VAZIO: 'Informe usuário e senha.',
  CABECALHO_VAZIO: 'Informe o nome e o valor do cabeçalho.',
};

export const ROTULO_DO_ESTADO_DO_EVENTO: Record<EstadoDoEvento, string> = {
  PENDENTE: 'Pendente',
  NOVA_TENTATIVA: 'Nova tentativa agendada',
  ENTREGUE: 'Entregue',
  FALHA: 'Falha',
  PAUSADA: 'Pausado',
};

export const VARIANTE_DO_ESTADO_DO_EVENTO: Record<
  EstadoDoEvento,
  'neutral' | 'success' | 'warning' | 'error' | 'info'
> = {
  PENDENTE: 'neutral',
  NOVA_TENTATIVA: 'info',
  ENTREGUE: 'success',
  FALHA: 'error',
  PAUSADA: 'warning',
};

/** O contrato manda o motivo e o erro como texto; valor que a tela não conhece aparece cru. */
export const ROTULO_DO_MOTIVO_DA_FALHA: Readonly<Record<string, string>> = {
  TENTATIVAS_ESGOTADAS: 'Tentativas esgotadas',
  ERRO_DEFINITIVO: 'Recusado pelo receptor',
  EXPIRADO_NA_PAUSA: 'Expirou durante a pausa',
  CORPO_AUSENTE: 'Conteúdo já apagado',
};

export const ROTULO_DO_ERRO_DA_TENTATIVA: Readonly<Record<string, string>> = {
  TIMEOUT: 'Sem resposta em 10 s',
  CONEXAO: 'Falha de conexão',
  ENDERECO_BLOQUEADO: 'Endereço não permitido',
  REDIRECIONAMENTO: 'Redirecionamento não é seguido',
  URL_INVALIDA: 'URL inválida',
};

/** WH-13 / P-W1: a pausa automática exige as duas condições ao mesmo tempo. */
export const LIMITE_DE_FALHAS_SEGUIDAS = 20;

/** Contrato v1, `/segredo`: depois de rotacionar, o anterior ainda assina por este prazo; rotacionar de novo descarta o mais antigo. */
export const DIAS_DE_VALIDADE_DO_SEGREDO_ANTERIOR = 7;

export const TEXTOS_DO_SEGREDO_DO_WEBHOOK: TextosDoSegredoExibido = {
  titulo: 'Segredo do webhook',
  instrucao:
    'Copie o segredo agora e configure no sistema que recebe os eventos: ele confere a assinatura de cada envio. O segredo não será exibido de novo.',
  avisoSemCopiar: {
    titulo: 'Você ainda não copiou o segredo',
    descricao:
      'Depois de fechar, não há como ver este segredo de novo. Se ele se perder, será preciso rotacionar.',
  },
  dicaDeCopiaManual: 'Selecione o texto do segredo e copie manualmente.',
  rotuloCopiado: 'Copiado',
};

export const NOTA_DO_SO_TESTE = 'Recebe só o evento de teste até o ambiente de testes existir.';

export const TAMANHO_DA_PAGINA = 50;

/** O histórico do core não pagina: vêm as últimas N alterações (máximo do contrato). */
export const LIMITE_DO_HISTORICO = 100;

export const ROTULO_DA_ACAO_AUDITADA: Readonly<Record<string, string>> = {
  CRIAR_INTEGRACAO: 'Integração criada',
  EDITAR_INTEGRACAO: 'Eventos alterados',
  EDITAR_DESTINO: 'Endereço editado',
  GERAR_SEGREDO: 'Segredo gerado',
  ROTACIONAR_SEGREDO: 'Segredo rotacionado',
  ENVIAR_TESTE: 'Teste enviado',
  ATIVAR: 'Endereço ativado',
  PAUSAR: 'Endereço pausado',
  RETOMAR: 'Endereço retomado',
  REENVIAR: 'Reenvio pedido',
};

/**
 * Na ordem de precedência do core: no lote, os grupos de recusa aparecem nesta ordem. "Pausado"
 * aponta o caminho (retomar), porque o reenvio não fura a pausa.
 */
export const ROTULO_DA_RECUSA_DO_REENVIO: Readonly<Record<MotivoDaRecusaDoReenvio, string>> = {
  EVENTO_INEXISTENTE: 'O evento não foi encontrado neste escritório.',
  DESTINO_PAUSADO:
    'O endereço está pausado. Retome o endereço na aba Integração e peça o reenvio de novo.',
  NAO_ESTA_EM_FALHA:
    'O evento não está com falha: já foi entregue, ainda tem tentativa agendada ou está guardado na pausa. Só eventos com falha podem ser reenviados.',
  SEM_CORPO:
    'O conteúdo do evento já foi apagado (fica guardado por 30 dias) e não há o que reenviar.',
  DESTINO_INATIVO:
    'O endereço não está ativo. Ative o endereço na aba Integração e peça o reenvio de novo.',
  EM_ANDAMENTO:
    'Já há um reenvio deste evento em andamento. O resultado aparece nas tentativas do evento.',
};

/** Teto do core por pedido: no modo endereço, o que passa disso volta em `restantes`. */
export const LIMITE_DO_REENVIO = 500;

export const ROTULO_DO_PAPEL: Readonly<Record<string, string>> = {
  MASTER_ADMIN: 'Administrador master',
  ADMIN: 'Administrador',
  ADMIN_ARBITRALIS: 'Equipe Arbitralis',
};
